import { useEffect, useState } from 'react';

import type { SearchTemplateData } from '@/state/searchTemplate/searchTemplateData';

/**
 * Applies a loaded template once, and enables save after that preview settles.
 * Waits out a backend or Redis outage so the apply does not arm a preview that
 * fires as soon as recovery clears the pause.
 */
export function useApplyLoadedSearchTemplate({
  searchTemplateData,
  hasTemplateErrors,
  handleApply,
  isPreviewFetching,
  previewsPaused,
}: {
  searchTemplateData: SearchTemplateData | undefined;
  hasTemplateErrors: boolean;
  handleApply: () => void;
  isPreviewFetching: boolean;
  previewsPaused: boolean;
}): { initialSaveSearchDisabled: boolean } {
  const [initialApplyCalled, setInitialApplyCalled] = useState(false);
  const [initialSaveSearchDisabled, setInitialSaveSearchDisabled] = useState(false);

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- apply the loaded template once, then enable save */
  useEffect(() => {
    if (
      previewsPaused ||
      searchTemplateData === undefined ||
      initialApplyCalled ||
      hasTemplateErrors
    ) {
      return;
    }
    handleApply();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- apply the loaded template once
    setInitialApplyCalled(true);
  }, [searchTemplateData, handleApply, initialApplyCalled, hasTemplateErrors, previewsPaused]);

  useEffect(() => {
    if (
      initialApplyCalled &&
      searchTemplateData !== undefined &&
      !isPreviewFetching &&
      !initialSaveSearchDisabled
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- enable save after the initial template apply
      setInitialSaveSearchDisabled(true);
    }
  }, [searchTemplateData, initialApplyCalled, initialSaveSearchDisabled, isPreviewFetching]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */

  return { initialSaveSearchDisabled };
}
